import React, { useState } from 'react';
import { useStore } from '../../store/useStore';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { MessageSquare, ListTodo, CheckCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { TaskForm } from '../tasks/TaskForm';
import { TaskList } from '../tasks/TaskList';

export const ManagerDashboard: React.FC = () => {
  const { currentUser, messages, tasks } = useStore();
  const [showTaskForm, setShowTaskForm] = useState(false);
  
  // Get tasks created by the manager (assigned to others)
  const createdTasks = tasks.filter(task => task.assignedBy === currentUser?.id);
  
  // Get department messages
  const departmentMessages = messages.filter(msg => 
    msg.departments.includes(currentUser?.department!)
  );

  // Calculate task statistics for tasks assigned to others
  const completedTasks = createdTasks.filter(task => task.status === 'COMPLETED').length;
  const pendingTasks = createdTasks.filter(task => task.status === 'PENDING').length;
  const inProgressTasks = createdTasks.filter(task => task.status === 'IN_PROGRESS').length;
  const totalTasks = createdTasks.length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img
              src={currentUser?.avatar}
              alt={currentUser?.name}
              className="w-16 h-16 rounded-full ring-4 ring-emerald-500 ring-offset-4"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Welcome, {currentUser?.name}</h1>
              <p className="text-gray-600">Department Manager</p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowTaskForm(true)}
            icon={<ListTodo className="w-4 h-4" />}
          >
            Create Task
          </Button>
        </div>

        {/* Task Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-blue-50">
            <div className="flex items-center gap-2 mb-2">
              <ListTodo className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-medium text-blue-700">Assigned Tasks</h3>
            </div>
            <p className="text-2xl font-bold text-blue-900">{totalTasks}</p>
            <p className="text-sm text-blue-600 mt-1">Tasks you've assigned</p>
          </Card>

          <Card className="p-4 bg-emerald-50">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-medium text-emerald-700">Completed</h3>
            </div>
            <p className="text-2xl font-bold text-emerald-900">{completedTasks}</p>
            <p className="text-sm text-emerald-600 mt-1">Tasks finished</p>
          </Card>

          <Card className="p-4 bg-amber-50">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-medium text-amber-700">In Progress</h3>
            </div>
            <p className="text-2xl font-bold text-amber-900">{inProgressTasks}</p>
            <p className="text-sm text-amber-600 mt-1">Tasks in progress</p>
          </Card>

          <Card className="p-4 bg-red-50">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-red-600" />
              <h3 className="text-sm font-medium text-red-700">Pending</h3>
            </div>
            <p className="text-2xl font-bold text-red-900">{pendingTasks}</p>
            <p className="text-sm text-red-600 mt-1">Need attention</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tasks Assigned Section */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <ListTodo className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Tasks You've Assigned</h2>
                <p className="text-sm text-gray-600">Monitor task progress</p>
              </div>
            </div>

            <div className="space-y-4">
              <TaskList tasks={createdTasks} showDepartment />
            </div>
          </Card>
          
          {/* Department Communications */}
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Department Communications</h2>
                <p className="text-sm text-gray-600">Recent messages and updates</p>
              </div>
            </div>

            <div className="space-y-4">
              {departmentMessages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-gray-50 rounded-lg"
                >
                  <h3 className="font-medium text-gray-900">{message.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{message.content}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <span>{message.sender.name}</span>
                    <span>•</span>
                    <span>{format(new Date(message.createdAt), 'MMM d, h:mm a')}</span>
                  </div>
                </motion.div>
              ))}

              {departmentMessages.length === 0 && (
                <p className="text-center py-8 text-gray-500">No messages yet</p>
              )}
            </div>
          </Card>
        </div>
      </div>

      {showTaskForm && (
        <TaskForm
          department={currentUser?.department}
          onSuccess={() => setShowTaskForm(false)}
        />
      )}
    </div>
  );
};